import React, { useState } from 'react';
import { useAuth } from '../context/AuthContext';
import { useNavigate, useLocation } from 'react-router-dom';

const LoginPage = () => {
  const { login } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [isLoggingIn, setIsLoggingIn] = useState(false);

  const destination = location.state?.from;
  const from = destination?.pathname
    ? destination.pathname + (destination.search || '') + (destination.hash || '') : '/dashboard';

  const handleLogin = async () => {
    if (!username || !password) {
      setError('Please enter both username and password');
      return;
    }
    setError('');
    setIsLoggingIn(true);
    try {
      await login(username, password);
      navigate(from, { replace: true });
    } catch (err) {
      // login() throws a specific message when the account is locked out
      // (HTTP 429) so the user knows to wait rather than retry immediately.
      setError(err?.message && err.message !== 'Login failed'
        ? err.message
        : 'Invalid username or password');
    } finally {
      setIsLoggingIn(false);
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter') handleLogin();
  };

  return (
    <div className="min-h-screen flex flex-col items-center justify-center bg-bg px-4">
      {/* Brand */}
      <div className="mb-8 text-center">
        <h1 className="text-4xl font-bold text-text tracking-tight">
          Ruby Form
        </h1>
        <p className="text-text-muted text-sm mt-2">Racing data platform</p>
      </div>

      {/* Login card */}
      <div className="w-full max-w-sm">
        <div className="card-padded space-y-5">
          {/* Error */}
          {error && (
            <div className="flex items-center gap-2 px-3 py-2 rounded-md bg-danger-muted text-danger text-sm">
              <svg className="w-4 h-4 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M12 9v2m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
              {error}
            </div>
          )}

          {/* Fields */}
          <div className="space-y-3">
            <div>
              <label htmlFor="username" className="block text-xs font-medium text-text-secondary mb-1.5">
                Username
              </label>
              <input
                id="username"
                type="text"
                className="input"
                value={username}
                onChange={(e) => setUsername(e.target.value)}
                onKeyDown={handleKeyDown}
                placeholder="Enter your username"
                autoFocus
                autoComplete="username"
              />
            </div>

            <div>
              <label htmlFor="password" className="block text-xs font-medium text-text-secondary mb-1.5">
                Password
              </label>
              <input
                id="password"
                type="password"
                className="input"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                onKeyDown={handleKeyDown}
                placeholder="Enter your password"
                autoComplete="current-password"
              />
            </div>
          </div>

          {/* Submit */}
          <button
            className="btn-primary w-full"
            onClick={handleLogin}
            disabled={isLoggingIn}
          >
            {isLoggingIn ? (
              <span className="flex items-center gap-2">
                <svg className="animate-spin h-4 w-4" viewBox="0 0 24 24" fill="none">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
                </svg>
                Signing in...
              </span>
            ) : (
              'Sign in'
            )}
          </button>
        </div>
      </div>

      {/* Footer */}
      <p className="mt-8 text-text-muted text-xs">
        &copy; {new Date().getFullYear()} Ruby Form
      </p>
    </div>
  );
};

export default LoginPage;
